import {
  CallbackFields,
  DynamicBlockFields,
  AppFields,
  appGQLQuery,
  AppInstallationFields,
  appInstallationsGQLQuery,
  appsGQLQuery,
  callbackGQLMutation,
  dynamicBlockGQLQuery,
  getAppNetworkSettingsGQLQuery,
  getAppSpaceSettingsGQLQuery,
  installAppGQLMutation,
  uninstallAppGQLMutation,
  updateAppNetworkSettingsGQLMutation,
  authMemberAppInstallationGQLQuery,
} from '../../graphql/index.js'
import {
  App,
  AppAction,
  AppInstallation,
  DynamicBlock,
  CallbackResponse,
  DynamicBlockSlateArgs,
  MutationCallbackArgs,
  MutationInstallAppArgs,
  MutationUninstallAppArgs,
  MutationUpdateAppNetworkSettingsArgs,
  PaginatedApp,
  PaginatedAppInstallation,
  QueryAppArgs,
  QueryAppsArgs,
  QueryDynamicBlockArgs,
  QueryGetAppNetworkSettingsArgs,
  QueryGetAppSpaceSettingsArgs,
  QueryGetNetworkAppInstallationsArgs,
  QueryAuthMemberAppInstallationArgs,
} from '../../types/index.js'
import { GraphqlClient } from '../graphql.client.js'

export class AppClient {
  private client: GraphqlClient

  constructor(client: GraphqlClient) {
    this.client = client
  }

  async get(
    variables: QueryAppArgs,
    fields: AppFields = 'basic',
    accessToken?: string,
  ): Promise<App> {
    type QueryResult = { app: App }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: appGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.app
  }

  async list(
    variables: QueryAppsArgs,
    fields: AppFields = 'basic',
    accessToken?: string,
  ): Promise<PaginatedApp> {
    type QueryResult = { apps: PaginatedApp }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: appsGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.apps
  }

  async networkSettings(
    variables: QueryGetAppNetworkSettingsArgs,
    accessToken?: string,
  ): Promise<string> {
    type QueryResult = { getAppNetworkSettings: string }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getAppNetworkSettingsGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.getAppNetworkSettings
  }

  async updateNetworkSettings(
    variables: MutationUpdateAppNetworkSettingsArgs,
    accessToken?: string,
  ): Promise<AppAction> {
    type QueryResult = { updateAppNetworkSettings: AppAction }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: updateAppNetworkSettingsGQLMutation(),
      variables,
      customToken: accessToken,
    })
    return result.updateAppNetworkSettings
  }

  async spaceSettings(
    variables: QueryGetAppSpaceSettingsArgs,
    accessToken?: string,
  ): Promise<string> {
    type QueryResult = { getAppSpaceSettings: string }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getAppSpaceSettingsGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.getAppSpaceSettings
  }

  async install(
    variables: MutationInstallAppArgs,
    fields: AppInstallationFields = 'basic',
    accessToken?: string,
  ): Promise<AppInstallation> {
    type QueryResult = { installApp: AppInstallation }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: installAppGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.installApp
  }

  async uninstall(
    variables: MutationUninstallAppArgs,
    fields: AppInstallationFields = 'basic',
    accessToken?: string,
  ): Promise<AppInstallation> {
    type QueryResult = { uninstallApp: AppInstallation }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: uninstallAppGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.uninstallApp
  }

  async appInstallations(
    variables: QueryGetNetworkAppInstallationsArgs,
    fields: AppInstallationFields = 'basic',
    accessToken?: string,
  ): Promise<PaginatedAppInstallation> {
    type QueryResult = { getNetworkAppInstallations: PaginatedAppInstallation }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: appInstallationsGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.getNetworkAppInstallations
  }

  async authMemberAppInstallation(
    variables: QueryAuthMemberAppInstallationArgs,
    fields: AppInstallationFields = 'basic',
    accessToken?: string,
  ): Promise<AppInstallation> {
    type QueryResult = { getAuthMemberAppInstallationByAppId: AppInstallation }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: authMemberAppInstallationGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.getAuthMemberAppInstallationByAppId
  }

  async getDynamicBlock(
    variables: QueryDynamicBlockArgs & DynamicBlockSlateArgs,
    fields: DynamicBlockFields = 'default',
    accessToken?: string,
  ): Promise<DynamicBlock> {
    type QueryResult = { dynamicBlock: DynamicBlock }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: dynamicBlockGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.dynamicBlock
  }

  async callback(
    variables: MutationCallbackArgs,
    fields: CallbackFields = 'default',
    accessToken?: string,
  ): Promise<CallbackResponse> {
    type QueryResult = { callback: CallbackResponse }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: callbackGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.callback
  }
}
